/**
 * wyvio-ui · NextActionCard
 * ─────────────────────────
 * Le cœur du cockpit de vente.
 * Une seule information, une seule décision, un seul CTA.
 *
 * L'idée : le logiciel décide de ce qui compte à cet instant précis
 * (débloquer, payer, préparer, livrer, célébrer) et la carte le pose
 * sous les yeux du commercial en moins de 3 secondes.
 *
 * Contrat volontairement pauvre pour rester une brique de design.
 */
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const TONE = {
  accent: {
    surface: "bg-white dark:bg-[#111111] border-[#EEF2F5] dark:border-[#262626]",
    eyebrow: "text-[#4a6b0f] dark:text-[#C8FF2F]",
    aura: "bg-[#F3FCE1] dark:bg-[#1F2A0A]",
    icon: "text-[#4a6b0f] dark:text-[#C8FF2F]",
    ring: "ring-[#C8FF2F]/50",
  },
  danger: {
    surface: "bg-white dark:bg-[#111111] border-[#FECACA] dark:border-[#3B0F0F]",
    eyebrow: "text-[#991B1B] dark:text-[#FCA5A5]",
    aura: "bg-[#FEE2E2] dark:bg-[#3B0F0F]",
    icon: "text-[#991B1B] dark:text-[#FCA5A5]",
    ring: "ring-[#EF4444]/40",
  },
  success: {
    surface: "bg-white dark:bg-[#111111] border-[#BBF7D0] dark:border-[#14351F]",
    eyebrow: "text-[#166534] dark:text-[#86EFAC]",
    aura: "bg-[#DCFCE7] dark:bg-[#14351F]",
    icon: "text-[#166534] dark:text-[#86EFAC]",
    ring: "ring-[#22C55E]/30",
  },
  neutral: {
    surface: "bg-white dark:bg-[#111111] border-[#EEF2F5] dark:border-[#262626]",
    eyebrow: "text-[#6B7280] dark:text-[#A3A3A3]",
    aura: "bg-[#F3F4F6] dark:bg-[#1A1A1A]",
    icon: "text-[#6B7280] dark:text-[#A3A3A3]",
    ring: "ring-black/5",
  },
};

export const NextActionCard = ({
  tone = "accent",
  eyebrow = "PROCHAINE ACTION",
  title,
  description,
  meta,
  icon: Icon,
  primaryCta,
  secondaryCta,
  testId = "next-action-card",
}) => {
  const t = TONE[tone] || TONE.accent;
  return (
    <section
      data-testid={testId}
      className={[
        "relative rounded-3xl border p-8 lg:p-10",
        "transition-all duration-300 ease-out",
        t.surface,
      ].join(" ")}
    >
      {/* Aura discrète — coin haut-gauche */}
      <div
        aria-hidden
        className={[
          "absolute -top-16 -left-16 h-40 w-40 rounded-full blur-3xl opacity-70 pointer-events-none",
          t.aura,
        ].join(" ")}
      />

      <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {Icon && (
              <div
                className={[
                  "h-9 w-9 rounded-2xl flex items-center justify-center shrink-0",
                  t.aura,
                  t.icon,
                ].join(" ")}
              >
                <Icon size={16} strokeWidth={2.25} />
              </div>
            )}
            <p
              className={[
                "text-[10.5px] font-semibold uppercase tracking-[0.18em]",
                t.eyebrow,
              ].join(" ")}
            >
              {eyebrow}
            </p>
          </div>

          <h2
            data-testid={`${testId}-title`}
            className="mt-4 text-[26px] sm:text-[32px] lg:text-[36px] leading-[1.05] font-semibold tracking-[-0.025em] text-[#0A0A0A] dark:text-white"
          >
            {title}
          </h2>

          {description && (
            <p
              data-testid={`${testId}-description`}
              className="mt-3 text-[14.5px] sm:text-[15.5px] leading-[1.5] text-[#6B7280] dark:text-[#A3A3A3] max-w-2xl"
            >
              {description}
            </p>
          )}

          {meta && <div className="mt-4">{meta}</div>}
        </div>

        {(primaryCta || secondaryCta) && (
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {secondaryCta && (
              <button
                onClick={secondaryCta.onClick}
                data-testid={secondaryCta.testId || `${testId}-secondary`}
                disabled={secondaryCta.disabled}
                className={[
                  "h-11 inline-flex items-center gap-2 rounded-full px-5 text-[13px] font-semibold",
                  "bg-white dark:bg-[#0A0A0A] border border-[#EEF2F5] dark:border-[#262626]",
                  "text-[#0A0A0A] dark:text-white",
                  "hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]",
                  "transition-all duration-200 ease-out disabled:opacity-50",
                ].join(" ")}
              >
                {secondaryCta.icon && <secondaryCta.icon size={14} strokeWidth={2.25} />}
                {secondaryCta.label}
              </button>
            )}
            {primaryCta && (
              <button
                onClick={primaryCta.onClick}
                data-testid={primaryCta.testId || `${testId}-primary`}
                disabled={primaryCta.disabled}
                className={[
                  "h-11 inline-flex items-center gap-2 rounded-full px-5 text-[13px] font-semibold",
                  tone === "success"
                    ? "bg-[#DCFCE7] text-[#166534] dark:bg-[#14351F] dark:text-[#86EFAC]"
                    : tone === "danger"
                    ? "bg-[#0A0A0A] text-white dark:bg-white dark:text-[#0A0A0A]"
                    : "bg-[#C8FF2F] text-[#0A0A0A]",
                  "hover:brightness-95 active:scale-[0.98] disabled:opacity-50",
                  "transition-all duration-200 ease-out",
                  "focus:outline-none focus-visible:ring-2",
                  t.ring,
                ].join(" ")}
              >
                {primaryCta.icon ? (
                  <primaryCta.icon size={14} strokeWidth={2.5} />
                ) : tone === "success" ? (
                  <CheckCircle2 size={14} strokeWidth={2.5} />
                ) : (
                  <Sparkles size={14} strokeWidth={2.5} />
                )}
                {primaryCta.label}
                <ArrowRight size={14} strokeWidth={2.5} className="opacity-70" />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
